import { useState, useCallback, useRef, useEffect } from "react";
import { extractCallDetails } from "../../../redux/slice/callLogSlice";
import { buildDetailingDataFromPayload } from "../callLogDialogUtils";
import {
  computeAutoSelectAfterExtract,
  mergeDetailApiFromExtractResult,
} from "../callLogDialogExtractLogic";

export function useCallLogDetailingFlow(dispatch, payload) {
  const [detailQuery, setDetailQuery] = useState("");
  const [isFetchingDetail, setIsFetchingDetail] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailApiData, setDetailApiData] = useState(null);
  const [isPromptSubmitted, setIsPromptSubmitted] = useState(false);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedKeyMessagesByProduct, setSelectedKeyMessagesByProduct] =
    useState({});
  const [selectedKeyMessages, setSelectedKeyMessages] = useState([]);

  const queryRef = useRef("");
  useEffect(() => {
    queryRef.current = detailQuery;
  }, [detailQuery]);

  const flattenSelectedKMs = useCallback((mapObj) => {
    const all = Object.values(mapObj || {}).flat();
    return Array.from(new Set(all));
  }, []);

  /** Supports React setState functional updates (DetailingPriorityTab). */
  const setKeyMessagesByProductAndFlatten = useCallback(
    (update) => {
      setSelectedKeyMessagesByProduct((prev) => {
        const next =
          typeof update === "function" ? update(prev) : update ?? {};
        setSelectedKeyMessages(flattenSelectedKMs(next));
        return next;
      });
    },
    [flattenSelectedKMs],
  );

  const handleSendPrompt = useCallback(async () => {
    const trimmed = (detailQuery ?? "").trim();
    if (!trimmed) {
      setDetailError("Please enter a prompt.");
      return;
    }

    setIsFetchingDetail(true);
    setDetailError("");

    try {
      const apiResult = await dispatch(
        extractCallDetails({
          repEmail: payload?.Rep_Email,
          call_text: trimmed,
        }),
      ).unwrap();

      let fallbackBuilt = null;
      try {
        fallbackBuilt = buildDetailingDataFromPayload(payload);
      } catch {
        fallbackBuilt = { salesGroup: payload?.Group || "", products: [] };
      }

      const { dataFromApi, mergedDetailApiData } =
        mergeDetailApiFromExtractResult(apiResult, fallbackBuilt);

      setDetailApiData(mergedDetailApiData);

      setSelectedProducts([]);
      setSelectedKeyMessagesByProduct({});
      setSelectedKeyMessages([]);

      if (dataFromApi) {
        const { selectedProducts: autoProducts, selectedKeyMessagesByProduct: autoKm } =
          computeAutoSelectAfterExtract(dataFromApi, mergedDetailApiData);

        if (autoProducts.length > 0) {
          setSelectedProducts(autoProducts);
        }

        if (autoKm && Object.keys(autoKm).length > 0) {
          setSelectedKeyMessagesByProduct((prev = {}) => {
            const next = { ...prev };
            Object.entries(autoKm).forEach(([prod, kms]) => {
              next[prod] = Array.from(new Set(kms));
            });
            const flattened = Array.from(new Set(Object.values(next).flat()));
            setSelectedKeyMessages(flattened);
            return next;
          });
        }
      }

      setIsPromptSubmitted(true);
    } catch (err) {
      console.error("extractCallDetails failed:", err);

      try {
        const built = buildDetailingDataFromPayload(payload);
        setDetailApiData(built);

        setSelectedProducts([]);
        setSelectedKeyMessagesByProduct({});
        setSelectedKeyMessages([]);

        setIsPromptSubmitted(true);
        setDetailError("");
      } catch (buildErr) {
        console.error("fallback build failed:", buildErr);
        setDetailError("Failed to load detailing options.");
        setIsPromptSubmitted(false);
        setDetailApiData(null);

        setSelectedProducts([]);
        setSelectedKeyMessagesByProduct({});
        setSelectedKeyMessages([]);
      }
    } finally {
      setIsFetchingDetail(false);
    }
  }, [detailQuery, dispatch, payload]);

  const resetDetailingState = useCallback(() => {
    setDetailQuery("");
    setDetailApiData(null);
    setDetailError("");
    setIsFetchingDetail(false);
    setIsPromptSubmitted(false);
    setSelectedProducts([]);
    setSelectedKeyMessagesByProduct({});
    setSelectedKeyMessages([]);
  }, []);

  return {
    detailQuery,
    setDetailQuery,
    queryRef,
    isFetchingDetail,
    detailError,
    setDetailError,
    detailApiData,
    setDetailApiData,
    isPromptSubmitted,
    setIsPromptSubmitted,
    selectedProducts,
    setSelectedProducts,
    selectedKeyMessagesByProduct,
    setSelectedKeyMessagesByProduct,
    selectedKeyMessages,
    setSelectedKeyMessages,
    flattenSelectedKMs,
    setKeyMessagesByProductAndFlatten,
    handleSendPrompt,
    resetDetailingState,
  };
}
